/**
 * 服务方（Provider）工作台接口客户端。
 *
 * 三条与「这是外部机构」直接相关的约定：
 *
 * ① **token 存 `hew3.provider.token`，与 C 端（`hew3.token`）、员工控制台
 *    （`hew3.admin.token`）刻意不同名。** 三端可能在同一台机器的同一浏览器里打开，
 *    同名 key 会让「服务方凭证被成员端读到」（或反之）—— 受众隔离（`aud: 'provider'`）
 *    就只剩服务端一侧，前端会出现难以排查的错乱登录态。
 *
 * ② **只调同源 `/api`**：开发态由 Vite 代理、生产态由本应用自己的 nginx 代理。
 *    服务方是外部机构，不依赖 CORS_ORIGINS 的配置正确性。
 *
 * ③ **错误一律照实呈现服务端原文**，界面不翻译、不软化：
 *    登录失败是「账号或口令不正确」（与账号不存在同一文案，防枚举），
 *    锁定是「账号因多次登录失败已锁定，请 N 分钟后重试」，
 *    非法的状态流转是 409 的中文说明（含「允许…的状态」清单）。
 *    任何一处替服务端改写文案，都会让服务方按错误的理解去操作。
 */

/** 与 C 端 `hew3.token`、控制台 `hew3.admin.token` 刻意不同名。 */
const TOKEN_KEY = 'hew3.provider.token';

/* ---------------- 服务端口径的类型（字段与 provider.routes.ts 的响应一一对应） ---------------- */

/**
 * 工单状态。取值与中文名都来自 `@hew3/shared` 的 `bookingStatuses` / `bookingStatusLabels`，
 * 前端**不另写一份映射**（一个 key 两个中文名是最难发现的文案级缺陷）。
 */
export type BookingStatus = 'pending' | 'matched' | 'scheduled' | 'serving' | 'done' | 'no_show' | 'cancelled';

/** 履约必需的成员视图：只有姓名与电话，**没有** fullName / memberNo 等档案字段（接口刻意不给）。 */
export interface ProviderMemberView {
  name: string;
  phone: string;
}

export interface ProviderServiceView {
  resourceName: string;
  slotLabel: string;
  startAt: string;
  endAt: string;
}

/** 履约时间轴的一条事件。 */
export interface BookingEvent {
  at: string;
  text: string;
}

/** 服务方默认能看到的**全部**工单内容（10 个键）。 */
export interface ProviderBooking {
  id: string;
  status: BookingStatus | string;
  statusLabel: string;
  member: ProviderMemberView;
  service: ProviderServiceView;
  memberNote: string;
  createdAt: string;
  updatedAt: string;
  timeline: BookingEvent[];
}

/**
 * 某数据域的授权。
 *
 * `fields` 是**字段名清单**（如 `healthScore`），不是数据本身 —— 界面据此说明
 * 「授权了哪些域」，具体值由 `AuthorizedData` 里对应的字段承载。
 */
export interface AuthorizedScope {
  scope: string;
  label: string;
  fields: string[];
}

/**
 * 成员**单独授权**后才下发的数据域。
 *
 * 这是平台隐私口径里唯一的例外：`grantedScopes` 为空时接口连 SQL 都不执行，
 * 响应里**根本没有这个对象**（不是空数组、不是 null）。界面因此必须区分：
 *   · 有 `authorizedData` → 显著标注「数据来源：成员授权」并列出 scopes；
 *   · 没有 → 写明「该成员未授权任何健康数据，因此本页不含健康信息」。
 * 把「未授权」渲染成空白，服务方会以为数据加载失败而反复重试。
 */
export interface AuthorizedData {
  dataSource: string;
  dataSourceNote: string;
  scopes: AuthorizedScope[];
  latestHealthScore?: { score: number; gradeText: string; measuredAt: string };
  physioVitals?: {
    code: string;
    name: string;
    valueText: string;
    unit: string;
    refRange: string;
    status: string;
    capturedAt: string;
  }[];
  enterpriseScore?: { score: number; gradeComment: string; measuredAt: string };
  wealthScore?: { score: number; gradeText: string; measuredAt: string };
}

/** 详情视图 = 履约必需字段 + （仅在成员授权时）授权数据域。 */
export interface ProviderBookingDetail extends ProviderBooking {
  /** 本次读取覆盖的数据域；未授权时为空数组（接口显式下发，不是缺省） */
  grantedScopes: string[];
  /** **未授权时该键不存在**（不是 null、不是空对象） */
  authorizedData?: AuthorizedData;
}

/**
 * 服务方的数据面自我说明。
 *
 * 接口把它放在列表响应里，就是为了让服务方**自己看到**「平台给了什么、没给什么」，
 * 而不是只能从一份外部文档里读到。界面因此把它整块呈现，不做摘要、不折叠。
 */
export interface DataPolicy {
  purpose: string;
  providedFields: string[];
  withheld: string;
}

export interface BookingsResult {
  items: ProviderBooking[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  dataPolicy: DataPolicy;
}

/** KPI：全部计数由服务端按主体名下工单算出，界面不自行累加。 */
export interface OverviewResult {
  kpi: {
    awaitingAccept: number;
    inProgress: number;
    today: number;
    completed: number;
    noShow: number;
    cancelled: number;
  };
  byStatus: Record<string, number>;
}

export interface ProviderSession {
  provider: {
    id: string;
    name: string;
    contactName: string;
    phone: string;
    lastLoginAt: string | null;
    /** 本期恒为 false：界面据此如实显示「未启用」，不宣称已开启二次验证 */
    totpEnabled: boolean;
  };
  subject: {
    id: string;
    name: string;
    subjectType: string;
    category: string;
    city: string;
    admissionStatus: string;
    bookable: boolean;
  };
  resources: { total: number; bookable: number };
  /** 授权来源说明（服务端原文）：服务方不继承任何员工权限点 */
  authorization: string;
}

export interface ProviderLoginResult {
  token: string;
  expiresInSeconds: number;
  provider: { id: string; name: string; contactName: string; subjectId: string; subjectName: string };
}

/* ---------------- token 存取 ---------------- */

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // 隐私模式下 localStorage 可能不可用，忽略即可（会话仍在内存中）
  }
}

/* ---------------- 错误 ---------------- */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let onUnauthorized: (() => void) | null = null;

/** 由 App 注册：token 失效（401）时自动回到登录页。 */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const err = (payload ?? {}) as { error?: string; message?: string; details?: unknown };
    if (response.status === 401) {
      setToken(null);
      onUnauthorized?.();
    }
    throw new ApiError(
      response.status,
      err.error ?? 'request_failed',
      err.message ?? `请求失败（${response.status}）`,
      err.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
};

/* ---------------- 服务方接口 ---------------- */

/**
 * 服务方登录：手机号 + 口令。
 *
 * 服务端对「口令错 / 账号不存在 / 账号停用 / 主体准入失效」返回**同一句话**
 * （「账号或口令不正确」），只有锁定会给出带分钟数的 403 —— 界面照实显示，
 * 不自作聪明地区分原因（那会把服务端的防枚举设计在界面上重新打开）。
 */
export function providerLogin(input: { phone: string; password: string }): Promise<ProviderLoginResult> {
  return api.post<ProviderLoginResult>('/api/provider/auth/login', input);
}

export const providerApi = {
  /** 机构信息 + 绑定主体（名称、准入状态、可约资源数） */
  me: (signal?: AbortSignal) => api.get<ProviderSession>('/api/provider/me', signal),
  /** KPI 总览 */
  overview: (signal?: AbortSignal) => api.get<OverviewResult>('/api/provider/overview', signal),
  /**
   * 工单列表。`status` / `from` / `to` 为空串表示不筛选
   * （from / to 允许 `YYYY-MM-DD`，服务端按日历日边界收敛）。
   */
  bookings: (filter: { status: string; from: string; to: string; page: number }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (filter.status) query.set('status', filter.status);
    if (filter.from) query.set('from', filter.from);
    if (filter.to) query.set('to', filter.to);
    query.set('page', String(filter.page));
    return api.get<BookingsResult>(`/api/provider/bookings?${query.toString()}`, signal);
  },
  booking: (id: string, signal?: AbortSignal) =>
    api.get<ProviderBookingDetail>(`/api/provider/bookings/${encodeURIComponent(id)}`, signal),

  /* ---- 履约动作：每一次流转都由服务端裁决，非法流转返回 409 的中文原文 ---- */

  /** 接单：`pending` / `scheduled` → `matched`（已是 matched 时幂等） */
  accept: (id: string) => api.post<ProviderBookingDetail>(`/api/provider/bookings/${encodeURIComponent(id)}/accept`),
  /** 开始履约：`matched` / `scheduled` → `serving`（不能从 `pending` 跳级） */
  start: (id: string) => api.post<ProviderBookingDetail>(`/api/provider/bookings/${encodeURIComponent(id)}/start`),
  /** 完成履约：`serving` → `done`（可带结论文本） */
  complete: (id: string, summary: string) =>
    api.post<ProviderBookingDetail>(`/api/provider/bookings/${encodeURIComponent(id)}/complete`, { summary }),
  /** 服务方取消：**原因必填**（服务端空原因返回 400），原因写入成员可见的履约时间轴 */
  cancel: (id: string, reason: string) =>
    api.post<ProviderBookingDetail>(`/api/provider/bookings/${encodeURIComponent(id)}/cancel`, { reason }),
  /** 成员未到场：**说明必填**；不退号（名额确实被占用过） */
  noShow: (id: string, note: string) =>
    api.post<ProviderBookingDetail>(`/api/provider/bookings/${encodeURIComponent(id)}/no-show`, { note }),

  logout: () => api.post<{ ok: boolean }>('/api/provider/auth/logout'),
};
